import { useEffect, useState, useRef } from "react";
import { ShieldCheck, Users, Newspaper, Loader2, Trash2, CheckCircle2, ImagePlus, Save, DoorOpen, DoorClosed, Settings as SettingsIcon, Database, GraduationCap, Plus, Pencil, X, TrendingUp, Award, Calendar, Megaphone, Bold, Italic, EyeOff, Smile, Heading1, Search, AtSign, ShieldAlert, Radio, Send } from "lucide-react";
import api from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { formatApiErrorDetail } from "../context/AuthContext";
import BackupPanel from "./BackupPanel";
import FpconPanel from "../components/FpconPanel";
import ServiceCallPanel from "../components/ServiceCallPanel";

// Tier degli esami: deve restare sincronizzato con server.EXAM_TIER_LABELS
const EXAM_TIER_OPTIONS = [
  { key: "vice_sceriffo", label: "Vice Sceriffo", threshold: 70 },
  { key: "sergente", label: "Sergente", threshold: 75 },
  { key: "tenente", label: "Tenente", threshold: 80 },
  { key: "capitano", label: "Capitano", threshold: 85 },
];
const tierLabel = (k) => EXAM_TIER_OPTIONS.find((t) => t.key === k)?.label || k;

export default function AreaComando() {
  return (
    <div className="space-y-8" data-testid="area-comando">
      <div>
        <div className="uppercase-label text-sheriff-gold mb-2 flex items-center gap-2">
          <ShieldCheck className="h-4 w-4" /> Area Comando
        </div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter text-[#F5E6B8]">
          Pannello di Comando
        </h1>
        <p className="text-[#D4C480] max-w-2xl mt-2">
          Gestione operativa: pubblicazione news e organizzazione eventi.
        </p>
      </div>

      <Tabs defaultValue="news" className="w-full">
        <TabsList className="bg-[#143830] border border-sheriff-gold/25 rounded-xl flex-wrap h-auto">
          <TabsTrigger value="news" data-testid="tab-news" className="rounded-xl uppercase-label">
            <Newspaper className="h-4 w-4 mr-2" /> Crea News
          </TabsTrigger>
          <TabsTrigger value="events" data-testid="tab-events" className="rounded-xl uppercase-label">
            <Calendar className="h-4 w-4 mr-2" /> Eventi
          </TabsTrigger>
          <TabsTrigger value="announcements" data-testid="tab-announcements" className="rounded-xl uppercase-label">
            <Megaphone className="h-4 w-4 mr-2" /> Annunci Discord
          </TabsTrigger>
          <TabsTrigger value="fpcon" data-testid="tab-fpcon" className="rounded-xl uppercase-label">
            <ShieldAlert className="h-4 w-4 mr-2" /> FPCON
          </TabsTrigger>
          <TabsTrigger value="service-call" data-testid="tab-service-call" className="rounded-xl uppercase-label">
            <Radio className="h-4 w-4 mr-2" /> Chiamata Servizio
          </TabsTrigger>
        </TabsList>
        <TabsContent value="news" className="mt-6">
          <NewsPanel />
        </TabsContent>
        <TabsContent value="events" className="mt-6">
          <EventsAdminPanel />
        </TabsContent>
        <TabsContent value="announcements" className="mt-6">
          <AnnouncementsPanel />
        </TabsContent>
        <TabsContent value="fpcon" className="mt-6">
          <FpconPanel />
        </TabsContent>
        <TabsContent value="service-call" className="mt-6">
          <ServiceCallPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [discordEditing, setDiscordEditing] = useState(null); // {user_id, rp_name, current}
  const [discordSaving, setDiscordSaving] = useState(false);
  // Dialog rinomina operatore: cambia rp_name (nome IC visibile ovunque + sync Discord)
  const [nameEditing, setNameEditing] = useState(null); // { user_id, current, draft }
  const [nameSaving, setNameSaving] = useState(false);
  // Dialog per degradazione: chiede motivazione obbligatoria
  const [demoteDialog, setDemoteDialog] = useState(null); // { user, fromRole, toRole }
  const [demoteReason, setDemoteReason] = useState("");
  const [demoteSaving, setDemoteSaving] = useState(false);

  const handleDiscordSave = async () => {
    const raw = (discordEditing?.draft || "").replace(/\D/g, "");
    if (!/^\d{15,25}$/.test(raw)) {
      toast.error("ID Discord non valido (15-25 cifre numeriche)");
      return;
    }
    setDiscordSaving(true);
    try {
      await api.patch(`/users/${discordEditing.user_id}`, { discord_id: raw });
      toast.success(`ID Discord di ${discordEditing.rp_name} aggiornato. Sincronizzazione ruolo eseguita.`);
      setDiscordEditing(null);
      reloadUsers();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Errore aggiornamento ID Discord");
    } finally {
      setDiscordSaving(false);
    }
  };

  const handleNameSave = async () => {
    const draft = (nameEditing?.draft || "").trim().replace(/\s+/g, " ");
    if (draft.length < 2) {
      toast.error("Nome troppo corto (min 2 caratteri)");
      return;
    }
    if (draft.length > 80) {
      toast.error("Nome troppo lungo (max 80 caratteri)");
      return;
    }
    if (draft === nameEditing.current) {
      setNameEditing(null);
      return;
    }
    setNameSaving(true);
    try {
      const { data } = await api.patch(`/users/${nameEditing.user_id}`, { rp_name: draft });
      const syncNote = data?._discord_sync?.ok
        ? "Nickname Discord aggiornato."
        : (data?._discord_sync ? " Sync Discord fallito — controllare permessi bot." : "");
      toast.success(`Nome operatore aggiornato: ${nameEditing.current} → ${draft}. ${syncNote}`);
      setNameEditing(null);
      reloadUsers();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Errore aggiornamento nome operatore");
    } finally {
      setNameSaving(false);
    }
  };

  const reloadUsers = async () => {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([api.get("/users"), api.get("/meta/roles")]);
      setUsers(u.data);
      setRoles(r.data.roles);
    } catch {
      toast.error("Errore caricamento utenti");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [u, r] = await Promise.all([api.get("/users"), api.get("/meta/roles")]);
        if (cancelled) return;
        setUsers(u.data);
        setRoles(r.data.roles);
      } catch {
        if (!cancelled) toast.error("Errore caricamento utenti");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleRoleChange = (u, newRole) => {
    if (!newRole || newRole === u.role) return;
    const oldIdx = roles.indexOf(u.role);
    const newIdx = roles.indexOf(newRole);
    // roles è in ordine decrescente di seniority (Sceriffo→Allievo)
    // → newIdx > oldIdx significa degradazione
    if (oldIdx >= 0 && newIdx > oldIdx) {
      setDemoteDialog({ user: u, fromRole: u.role, toRole: newRole });
      setDemoteReason("");
      return;
    }
    patchUser(u.id, { role: newRole });
  };

  const confirmDemote = async () => {
    if (!demoteDialog) return;
    if (demoteReason.trim().length < 10) {
      toast.error("Motivazione richiesta (min 10 caratteri)");
      return;
    }
    setDemoteSaving(true);
    try {
      await patchUser(demoteDialog.user.id, {
        role: demoteDialog.toRole,
        demotion_reason: demoteReason.trim(),
      }, false);
      toast.success(`${demoteDialog.user.rp_name}: degradato a ${demoteDialog.toRole}. Annuncio Discord inviato.`, { duration: 8000 });
      setDemoteDialog(null);
      setDemoteReason("");
    } catch (e) {
      toast.error(`Errore: ${e?.response?.data?.detail || "degradazione fallita"}`);
    } finally {
      setDemoteSaving(false);
    }
  };

  const patchUser = async (id, patch, optimistic = true) => {
    if (optimistic) setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, ...patch } : u)));
    try {
      const { data } = await api.patch(`/users/${id}`, patch);
      // Sync Discord risultato: mostra warning se è fallito (es. bot ruolo sotto al target)
      const sync = data?._discord_sync;
      if (sync && sync.ok === false) {
        const reason = sync.reason || "errore sconosciuto";
        if (reason.includes("missing_permissions")) {
          toast.warning(
            "Discord: ruolo/nickname NON cambiati. Il bot LSSD ha gerarchia insufficiente — su Discord vai in Server Settings → Roles e SPOSTA il ruolo del bot SOPRA tutti i ruoli LSSD (Sceriffo, Sotto Sceriffo, Assistente Sceriffo, Capo Divisione…). 15s di lavoro, poi rifai la promozione.",
            { duration: 15000 }
          );
        } else {
          toast.warning(`Sync Discord fallito: ${reason}`, { duration: 8000 });
        }
      }
      // ripulisci il campo interno prima di metterlo nello state
      const clean = { ...data }; delete clean._discord_sync;
      setUsers((prev) => prev.map((u) => (u.id === id ? clean : u)));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Aggiornamento fallito");
      reloadUsers();
      throw e;
    }
  };

  const deleteUser = async (id) => {
    // Prompt per motivazione licenziamento (min 10 caratteri)
    const rawReason = window.prompt(
      "LICENZIAMENTO OPERATORE\n\nInserisci la motivazione del licenziamento (min. 10 caratteri).\nIl bot Discord invierà una notifica ufficiale nel canale dedicato taggando l'operatore prima dell'eliminazione."
    );
    if (rawReason === null) return; // annullato
    const reason = rawReason.trim();
    if (reason.length < 10) {
      toast.error("Motivazione troppo corta (min. 10 caratteri)");
      return;
    }
    if (!window.confirm(`Confermi il licenziamento con motivazione:\n"${reason}"\n\nQuesta azione è IRREVERSIBILE.`)) return;
    try {
      await api.delete(`/users/${id}`, { params: { reason } });
      setUsers((prev) => prev.filter((u) => u.id !== id));
      toast.success("Utente licenziato e notifica Discord inviata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile eliminare l'utente");
    }
  };

  const [syncingNicks, setSyncingNicks] = useState(false);
  const [syncingHier, setSyncingHier] = useState(false);
  const [botStatus, setBotStatus] = useState(null); // { online, enabled, token_configured, ... }

  useEffect(() => {
    let mounted = true;
    const fetchStatus = async () => {
      try {
        const { data } = await api.get("/admin/discord/status");
        if (mounted) setBotStatus(data);
      } catch {
        if (mounted) setBotStatus({ online: false, reason: "request_failed" });
      }
    };
    fetchStatus();
    const id = setInterval(fetchStatus, 15000); // poll ogni 15s
    return () => { mounted = false; clearInterval(id); };
  }, []);

  const syncDiscordNicknames = async () => {
    if (!confirm("Aggiornare gli acronimi nei nickname Discord di tutti gli operatori approvati? L'operazione richiede ~1 secondo per utente.")) return;
    setSyncingNicks(true);
    try {
      const { data } = await api.post("/admin/discord/refresh-nicknames");
      if (data?.ok) {
        toast.success(`Nickname Discord sincronizzati: ${data.updated?.length || 0} aggiornati, ${data.skipped?.length || 0} saltati, ${data.failed?.length || 0} falliti`);
      } else {
        toast.error(`Errore: ${data?.reason || "sync fallita"}`);
      }
    } catch (e) {
      toast.error(`Errore: ${formatApiErrorDetail(e?.response?.data?.detail) || "richiesta fallita"}`);
    } finally {
      setSyncingNicks(false);
    }
  };

  const refreshDiscordHierarchy = async () => {
    setSyncingHier(true);
    try {
      const { data } = await api.post("/admin/discord/refresh-hierarchy");
      if (data?.ok) {
        const excludedRole = data.excluded_by_role || [];
        const excludedEmail = data.excluded_by_email || [];
        const total = data.total ?? "?";
        const scanned = data.scanned ?? "?";
        if (excludedRole.length > 0) {
          // Mostra i nomi degli esclusi così l'utente capisce perché qualcuno non appare
          const names = excludedRole.slice(0, 5).map(e => `${e.rp_name} (ruolo=${e.role_in_db})`).join(", ");
          const more = excludedRole.length > 5 ? ` +${excludedRole.length - 5} altri` : "";
          toast.warning(
            `Gerarchia aggiornata: ${total}/${scanned} approvati inclusi. ESCLUSI per ruolo non valido: ${names}${more}`,
            { duration: 12000 }
          );
        } else {
          toast.success(`Embed gerarchia Discord aggiornato — ${total} operatori inclusi (scan ${scanned}, esclusi system: ${excludedEmail.length})`);
        }
      } else {
        toast.error(`Errore: ${data?.reason || "refresh fallito"}`);
      }
    } catch (e) {
      toast.error(`Errore: ${formatApiErrorDetail(e?.response?.data?.detail) || "richiesta fallita"}`);
    } finally {
      setSyncingHier(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#143830] border border-sheriff-gold/25 p-12 rounded-xl text-center">
        <Loader2 className="h-8 w-8 animate-spin text-sheriff-gold mx-auto" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar Discord sync */}
      <div className="flex flex-wrap items-center justify-end gap-2" data-testid="discord-toolbar">
        {botStatus && (
          <div
            className={`flex items-center gap-2 px-3 h-9 rounded-full border text-xs font-bold uppercase tracking-[0.12em] ${
              botStatus.online
                ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300"
                : "border-red-400/40 bg-red-400/10 text-red-300"
            }`}
            title={botStatus.online ? `${botStatus.user || "bot"} · ${botStatus.latency_ms}ms · guild=${botStatus.main_guild || "—"}` : (botStatus.reason || "Bot offline")}
            data-testid="discord-bot-status"
          >
            <span className={`w-2 h-2 rounded-full ${botStatus.online ? "bg-emerald-400 animate-pulse" : "bg-red-400"}`} />
            BOT {botStatus.online ? "Online" : "Offline"}
          </div>
        )}
        <Button
          onClick={syncDiscordNicknames}
          disabled={syncingNicks}
          variant="outline"
          className="bg-[#143830] border-sheriff-gold/40 text-sheriff-gold hover:bg-sheriff-gold/15 hover:border-sheriff-gold rounded-full uppercase-label text-xs h-9"
          data-testid="btn-sync-discord-nicknames"
        >
          {syncingNicks ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Users className="h-3.5 w-3.5 mr-2" />}
          Sync Nickname Discord
        </Button>
        <Button
          onClick={refreshDiscordHierarchy}
          disabled={syncingHier}
          variant="outline"
          className="bg-[#143830] border-sheriff-gold/40 text-sheriff-gold hover:bg-sheriff-gold/15 hover:border-sheriff-gold rounded-full uppercase-label text-xs h-9"
          data-testid="btn-refresh-discord-hierarchy"
        >
          {syncingHier ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5 mr-2" />}
          Refresh Gerarchia Discord
        </Button>
      </div>
      <div className="bg-[#143830] border border-sheriff-gold/25 rounded-xl overflow-x-auto" data-testid="users-panel">
      <Table>
        <TableHeader className="bg-[#0F2620]">
          <TableRow>
            <TableHead className="uppercase-label">Operatore</TableHead>
            <TableHead className="uppercase-label">Email</TableHead>
            <TableHead className="uppercase-label">Discord</TableHead>
            <TableHead className="uppercase-label w-56">Ruolo</TableHead>
            <TableHead className="uppercase-label text-center">HR</TableHead>
            <TableHead className="uppercase-label text-center">Agenti</TableHead>
            <TableHead className="uppercase-label text-center">Detective</TableHead>
            <TableHead className="uppercase-label text-center">Matricola</TableHead>
            <TableHead className="uppercase-label text-center">Approvato</TableHead>
            <TableHead className="uppercase-label text-right">Azioni</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((u) => (
            <TableRow key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-[#0F2620]">
              <TableCell>
                <button
                  type="button"
                  data-testid={`edit-name-${u.id}`}
                  onClick={() => setNameEditing({ user_id: u.id, current: u.rp_name || "", draft: u.rp_name || "" })}
                  className="group inline-flex items-center gap-1.5 text-left hover:bg-sheriff-gold/5 rounded px-1.5 -mx-1.5 py-0.5 transition-colors"
                  title="Clicca per modificare il nome IC"
                >
                  <span className="font-semibold">{u.rp_name}</span>
                  <Pencil className="h-3 w-3 text-sheriff-gold/50 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
                <div className="text-xs text-[#B8A269] font-mono-codes">{u.is_command ? "COMANDO" : "OPERATORE"}</div>
              </TableCell>
              <TableCell className="font-mono-codes text-xs text-[#D4C480]">{u.email}</TableCell>
              <TableCell>
                <button
                  type="button"
                  onClick={() => setDiscordEditing({ user_id: u.id, rp_name: u.rp_name, current: u.discord_id || "", draft: u.discord_id || "" })}
                  data-testid={`discord-cell-${u.id}`}
                  className="group inline-flex items-center gap-1.5 text-left"
                  title="Clicca per modificare l'ID Discord"
                >
                  {u.discord_id ? (
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[10px] font-mono group-hover:bg-emerald-500/25 transition">
                      <CheckCircle2 className="h-3 w-3" /> {u.discord_id.slice(0, 6)}...{u.discord_id.slice(-4)}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300 text-[10px] font-bold uppercase tracking-wider group-hover:bg-amber-500/25 transition">
                      <Pencil className="h-3 w-3" /> Mancante
                    </span>
                  )}
                </button>
              </TableCell>
              <TableCell>
                <Select
                  value={u.role || ""}
                  onValueChange={(v) => handleRoleChange(u, v)}
                >
                  <SelectTrigger data-testid={`role-select-${u.id}`} className="rounded-xl">
                    <SelectValue placeholder="Seleziona ruolo" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell className="text-center">
                <Switch
                  data-testid={`switch-hr-${u.id}`}
                  checked={!!u.perm_hr}
                  onCheckedChange={(v) => patchUser(u.id, { perm_hr: v })}
                />
              </TableCell>
              <TableCell className="text-center">
                <Switch
                  data-testid={`switch-agents-${u.id}`}
                  checked={!!u.perm_agents}
                  onCheckedChange={(v) => patchUser(u.id, { perm_agents: v })}
                />
              </TableCell>
              <TableCell className="text-center">
                <Switch
                  data-testid={`switch-detective-${u.id}`}
                  checked={!!u.perm_detective_bureau}
                  onCheckedChange={(v) => patchUser(u.id, { perm_detective_bureau: v })}
                />
              </TableCell>
              <TableCell className="text-center">
                <BadgeNumberInput user={u} onSave={(bn) => patchUser(u.id, { badge_number: bn })} />
              </TableCell>
              <TableCell className="text-center">
                {u.is_approved ? (
                  <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-xl" data-testid={`approved-${u.id}`}>
                    <CheckCircle2 className="h-3 w-3 mr-1" /> Sì
                  </Badge>
                ) : (
                  <Button
                    data-testid={`approve-btn-${u.id}`}
                    size="sm"
                    className="bg-sheriff-green hover:bg-sheriff-green-dark text-white rounded-xl uppercase-label"
                    onClick={() => patchUser(u.id, { is_approved: true })}
                  >
                    Approva
                  </Button>
                )}
              </TableCell>
              <TableCell className="text-right">
                <Button
                  data-testid={`delete-user-${u.id}`}
                  variant="ghost"
                  size="sm"
                  className="text-rose-600 hover:bg-rose-50"
                  onClick={() => deleteUser(u.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Dialog Rinomina Operatore */}
      <Dialog open={!!nameEditing} onOpenChange={(o) => !o && setNameEditing(null)}>
        <DialogContent className="max-w-md bg-gradient-to-br from-[#143830] to-[#0A1F18] border-sheriff-gold/40 text-[#F5E6B8] rounded-2xl" data-testid="name-edit-dialog">
          {nameEditing && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-[#F5E6B8]">
                  Modifica Nome IC
                </DialogTitle>
                <DialogDescription className="text-[#B8A269]">
                  Cambia il Nome e Cognome IC di questo operatore. Il nuovo nome sarà visibile ovunque (roster, profilo pubblico, servizio, Hall of Fame) e il <span className="text-sheriff-gold font-semibold">nickname Discord verrà ricostruito automaticamente</span> con il nuovo nome (prefisso grado + matricola + nome).
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div>
                  <Label className="text-sheriff-gold uppercase-label">Nome attuale</Label>
                  <div className="text-[#F5E6B8]/80 font-mono text-sm mt-1 px-3 py-2 rounded-lg bg-[#0A1F18] border border-sheriff-gold/15">
                    {nameEditing.current || <span className="italic opacity-60">— (vuoto)</span>}
                  </div>
                </div>
                <div>
                  <Label className="text-sheriff-gold uppercase-label">Nuovo Nome IC</Label>
                  <Input
                    data-testid="name-edit-input"
                    type="text"
                    value={nameEditing.draft}
                    onChange={(e) => setNameEditing({ ...nameEditing, draft: e.target.value })}
                    placeholder="es. John Marston"
                    maxLength={80}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !nameSaving) { e.preventDefault(); handleNameSave(); }
                    }}
                  />
                  <p className="text-[10px] text-[#8B7B49] leading-snug mt-2">
                    2-80 caratteri. Gli spazi multipli verranno collassati automaticamente.
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setNameEditing(null)} className="border-sheriff-gold/40 text-sheriff-gold rounded-full">Annulla</Button>
                <Button
                  onClick={handleNameSave}
                  disabled={nameSaving || nameEditing.draft.trim() === nameEditing.current || nameEditing.draft.trim().length < 2}
                  data-testid="save-name-btn"
                  className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] font-bold rounded-full"
                >
                  {nameSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Salva e sincronizza
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!discordEditing} onOpenChange={(o) => !o && setDiscordEditing(null)}>
        <DialogContent className="max-w-md bg-gradient-to-br from-[#143830] to-[#0A1F18] border-sheriff-gold/40 text-[#F5E6B8] rounded-2xl" data-testid="discord-edit-dialog">
          {discordEditing && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-[#F5E6B8]">
                  ID Discord — {discordEditing.rp_name}
                </DialogTitle>
                <DialogDescription className="text-[#B8A269]">
                  Inserisci o modifica l&apos;ID Discord di questo operatore. Il salvataggio sincronizza automaticamente il suo grado sul server Discord ed è visibile anche nella sua sezione &quot;Il mio profilo&quot;.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2 py-2">
                <Label className="text-sheriff-gold uppercase-label">ID Discord (15-25 cifre)</Label>
                <Input
                  data-testid="discord-edit-input"
                  type="text"
                  inputMode="numeric"
                  value={discordEditing.draft}
                  onChange={(e) => setDiscordEditing({ ...discordEditing, draft: e.target.value.replace(/\D/g, "") })}
                  placeholder="es. 351876452198482432"
                  autoFocus
                />
                <p className="text-[10px] text-[#8B7B49] leading-snug mt-2">
                  Su Discord: click destro sull&apos;utente nel server → <span className="text-sheriff-gold">Copia ID utente</span> (modalità sviluppatore deve essere attiva).
                </p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDiscordEditing(null)} className="border-sheriff-gold/40 text-sheriff-gold rounded-full">Annulla</Button>
                <Button
                  onClick={handleDiscordSave}
                  disabled={discordSaving || discordEditing.draft === discordEditing.current}
                  data-testid="save-discord-id-btn"
                  className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] font-bold rounded-full"
                >
                  {discordSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Salva e sincronizza
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Degradazione: motivazione obbligatoria */}
      <Dialog open={!!demoteDialog} onOpenChange={(o) => { if (!o) { setDemoteDialog(null); setDemoteReason(""); } }}>
        <DialogContent className="bg-[#0F2620] border-rose-500/40 max-w-md" data-testid="demote-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-300 font-heading flex items-center gap-2">
              <TrendingUp className="h-5 w-5 rotate-180" /> Degradazione · {demoteDialog?.user?.rp_name}
            </DialogTitle>
          </DialogHeader>
          {demoteDialog && (
            <div className="space-y-4">
              <div className="rounded-lg bg-rose-500/10 border border-rose-500/40 p-4">
                <div className="text-sm text-[#D4C480]">Stai per degradare:</div>
                <div className="text-[#F5E6B8] font-bold text-base mt-1">{demoteDialog.user.rp_name}</div>
                <div className="text-xs text-[#D4C480] mt-1.5 flex flex-wrap gap-2">
                  <span><strong className="text-sheriff-gold">{demoteDialog.fromRole}</strong></span>
                  <span>→</span>
                  <span className="text-rose-300 font-bold">{demoteDialog.toRole}</span>
                </div>
              </div>
              <p className="text-sm text-[#D4C480] leading-relaxed">
                Verranno eseguite queste azioni:
                <br/>· Ruolo aggiornato sul portale + sincronizzato su Discord
                <br/>· <strong className="text-rose-300">Annuncio pubblicato</strong> sul canale Discord delle degradazioni
                <br/>· Tracciato nell&apos;audit log (azione: <code className="text-rose-300 text-[10px]">demote</code>)
              </p>
              <div>
                <Label className="uppercase-label text-[10px] text-rose-300 mb-1.5 block">Motivazione (min 10 caratteri)</Label>
                <Textarea
                  value={demoteReason}
                  onChange={(e) => setDemoteReason(e.target.value)}
                  rows={4}
                  placeholder="Es. Reiterati comportamenti non conformi al regolamento durante il servizio, mancata responsabilità nel coordinamento delle pattuglie..."
                  className="bg-[#0A1F18] border-rose-500/30 text-[#F5E6B8] placeholder:text-[#8B7B49]"
                  data-testid="demote-reason-input"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button onClick={() => { setDemoteDialog(null); setDemoteReason(""); }} variant="ghost" className="text-[#D4C480]">Annulla</Button>
                <Button
                  onClick={confirmDemote}
                  disabled={demoteSaving || demoteReason.trim().length < 10}
                  className="bg-rose-500 hover:bg-rose-600 text-white font-bold disabled:opacity-50"
                  data-testid="demote-confirm-btn"
                >
                  {demoteSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <TrendingUp className="h-4 w-4 mr-2 rotate-180" />}
                  Conferma Degradazione
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
    </div>
  );
}

export function NewsPanel() {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [news, setNews] = useState([]);
  const fileRef = useRef(null);

  const loadNews = async () => {
    try {
      const { data } = await api.get("/news");
      setNews(data);
    } catch {
      toast.error("Errore caricamento news");
    }
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/news");
        if (!cancelled) setNews(data);
      } catch {
        if (!cancelled) toast.error("Errore caricamento news");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/news", { title, content });
      if (imageFile) {
        const fd = new FormData();
        fd.append("file", imageFile);
        await api.post(`/news/${data.id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("News pubblicata");
      setTitle("");
      setContent("");
      setImageFile(null);
      if (fileRef.current) fileRef.current.value = "";
      loadNews();
    } catch (err) {
      toast.error("Errore pubblicazione news");
    } finally {
      setSubmitting(false);
    }
  };

  const removeNews = async (id) => {
    if (!confirm("Eliminare questa news?")) return;
    try {
      await api.delete(`/news/${id}`);
      setNews((prev) => prev.filter((n) => n.id !== id));
      toast.success("News eliminata");
    } catch {
      toast.error("Errore");
    }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6" data-testid="news-panel">
      <form onSubmit={submit} className="lg:col-span-2 bg-[#143830] border border-sheriff-gold/25 rounded-xl p-6 space-y-4 h-fit" data-testid="news-form">
        <h3 className="font-heading font-bold text-lg mb-1">Nuovo Comunicato</h3>
        <p className="text-sm text-[#B8A269]">Apparirà immediatamente sulla Home pubblica.</p>

        <div>
          <Label className="text-xs font-semibold mb-1.5 block">Titolo</Label>
          <Input data-testid="news-title" required value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titolo del comunicato" />
        </div>
        <div>
          <Label className="text-xs font-semibold mb-1.5 block">Contenuto</Label>
          <Textarea data-testid="news-content" required value={content} onChange={(e) => setContent(e.target.value)} placeholder="Testo del comunicato..." rows={8} />
        </div>
        <div>
          <Label className="text-xs font-semibold mb-1.5 block">Immagine di copertina (opzionale)</Label>
          <div className="flex items-center gap-3">
            <input
              ref={fileRef}
              data-testid="news-image-input"
              type="file"
              accept="image/*"
              onChange={(e) => setImageFile(e.target.files?.[0] || null)}
              className="text-sm flex-1 file:mr-3 file:py-2 file:px-3 file:rounded-xl file:border-0 file:bg-sheriff-gold file:text-[#0A1F18] file:text-white file:uppercase-label file:cursor-pointer cursor-pointer"
            />
            {imageFile && <ImagePlus className="h-5 w-5 text-sheriff-gold" />}
          </div>
        </div>

        <Button
          type="submit"
          disabled={submitting}
          data-testid="news-submit"
          className="w-full bg-sheriff-green hover:bg-sheriff-green-dark text-white rounded-full uppercase-label font-bold h-11"
        >
          {submitting ? "Pubblicazione..." : (<><Save className="h-4 w-4 mr-2" /> Pubblica News</>)}
        </Button>
      </form>

      <div className="lg:col-span-3 space-y-3">
        <div className="uppercase-label text-[#B8A269]">News pubblicate ({news.length})</div>
        {news.length === 0 ? (
          <div className="bg-[#143830] border border-sheriff-gold/25 rounded-xl p-8 text-center text-sm text-[#B8A269]" data-testid="news-empty">
            Nessuna news pubblicata.
          </div>
        ) : (
          news.map((n) => (
            <div key={n.id} data-testid={`admin-news-${n.id}`} className="bg-[#143830] border border-sheriff-gold/25 rounded-xl p-4 flex items-start gap-4">
              {n.image_path && (
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/news/image/${n.image_path}`} alt="" className="h-16 w-24 object-cover rounded-xl border" onError={(e) => (e.currentTarget.style.display = "none")} />
              )}
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{n.title}</div>
                <div className="text-xs text-[#B8A269] font-mono-codes">
                  {new Date(n.created_at).toLocaleString("it-IT")} ·{" "}
                  {n.author_role && <span className="text-sheriff-gold font-bold">{n.author_role} · </span>}
                  {n.author}
                </div>
                <div className="text-sm text-[#D4C480] mt-1 line-clamp-2">{n.content}</div>
              </div>
              <Button data-testid={`delete-news-${n.id}`} variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50" onClick={() => removeNews(n.id)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}


export function RecruitmentSettingsPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const reloadSettings = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/recruitment");
      setStatus(data);
      setMessage(data.message || "");
    } catch {
      toast.error("Errore caricamento impostazioni");
    } finally { setLoading(false); }
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/settings/recruitment");
        if (cancelled) return;
        setStatus(data);
        setMessage(data.message || "");
      } catch {
        if (!cancelled) toast.error("Errore caricamento impostazioni");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggle = async (newOpen) => {
    setSaving(true);
    try {
      await api.post("/settings/recruitment", { open: newOpen, message: newOpen ? null : message });
      toast.success(newOpen ? "Bandi aperti" : "Bandi chiusi");
      reloadSettings();
    } catch {
      toast.error("Errore aggiornamento");
    } finally { setSaving(false); }
  };

  const saveMessage = async () => {
    setSaving(true);
    try {
      await api.post("/settings/recruitment", { open: !!status?.open, message });
      toast.success("Messaggio aggiornato");
      reloadSettings();
    } catch {
      toast.error("Errore aggiornamento");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="bg-[#143830] border border-sheriff-gold/25 rounded-xl p-12 text-center"><Loader2 className="h-8 w-8 animate-spin text-sheriff-gold mx-auto" /></div>;

  const isOpen = !!status?.open;
  return (
    <div className="max-w-2xl space-y-6" data-testid="recruitment-settings-panel">
      <div className={`relative overflow-hidden rounded-2xl border-2 p-8 ${isOpen ? "border-emerald-500/40 bg-emerald-500/5" : "border-rose-500/40 bg-rose-500/5"}`}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            <div className={`h-14 w-14 rounded-full flex items-center justify-center ${isOpen ? "bg-emerald-500/20 text-emerald-300" : "bg-rose-500/20 text-rose-300"}`}>
              {isOpen ? <DoorOpen className="h-7 w-7" /> : <DoorClosed className="h-7 w-7" />}
            </div>
            <div>
              <div className="uppercase tracking-[0.2em] text-[10px] font-bold text-sheriff-gold mb-1">Stato Bandi Reclutamento</div>
              <h3 className={`font-heading font-black text-3xl ${isOpen ? "text-emerald-300" : "text-rose-300"}`} data-testid="recruitment-status-label">
                {isOpen ? "APERTI" : "CHIUSI"}
              </h3>
              <p className="text-[#D4C480] text-sm mt-1">
                {isOpen ? "I cittadini possono inviare candidature." : "Le nuove candidature sono bloccate."}
              </p>
            </div>
          </div>
          <Button
            onClick={() => toggle(!isOpen)}
            disabled={saving}
            data-testid="toggle-recruitment-btn"
            className={`rounded-full uppercase-label font-bold ${isOpen ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"} text-white`}
          >
            {isOpen ? <><DoorClosed className="h-4 w-4 mr-2" /> Chiudi Bandi</> : <><DoorOpen className="h-4 w-4 mr-2" /> Apri Bandi</>}
          </Button>
        </div>
        {status?.updated_by && status?.updated_at && (
          <div className="mt-5 pt-4 border-t border-sheriff-gold/15 text-xs text-[#B8A269] font-mono">
            Ultimo aggiornamento: {new Date(status.updated_at).toLocaleString("it-IT")} · {status.updated_by}
          </div>
        )}
      </div>

      <div className="bg-[#143830] border border-sheriff-gold/25 rounded-2xl p-6">
        {isOpen ? (
          <div className="text-center py-4">
            <div className="uppercase tracking-[0.15em] text-xs font-bold text-sheriff-gold/60 mb-2">Messaggio di Chiusura</div>
            <p className="text-sm text-[#B8A269]">
              Il messaggio di chiusura sarà disponibile dopo aver chiuso i bandi.
            </p>
          </div>
        ) : (
          <>
            <Label className="uppercase tracking-[0.15em] text-xs font-bold text-sheriff-gold mb-2 block">Messaggio mostrato a Bandi Chiusi (opzionale)</Label>
            <Textarea
              data-testid="recruitment-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder="Es. I bandi torneranno aperti il prossimo weekend dopo la riunione di Comando."
              className="bg-[#0A1F18] border-sheriff-gold/30 rounded-xl mb-3"
            />
            <Button onClick={saveMessage} disabled={saving} className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] rounded-full uppercase-label font-bold" data-testid="save-message-btn">
              <Save className="h-4 w-4 mr-2" /> Salva Messaggio
            </Button>
          </>
        )}
      </div>
    </div>
  );
}


// ============================================================
// ESAME ADMIN PANEL — gestione domande (i risultati sono in Area HR)
// ============================================================

export function AgentSessionsPanel() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(null); // tier object in apertura
  const [slotsInput, setSlotsInput] = useState("3");
  const [noteInput, setNoteInput] = useState("");
  const [actioning, setActioning] = useState(false);

  const reload = async () => {
    try {
      const { data } = await api.get("/quiz/admin/sessions");
      setSessions(data);
    } catch {
      toast.error("Errore caricamento bandi");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/quiz/admin/sessions");
        if (!cancelled) setSessions(data);
      } catch {
        if (!cancelled) toast.error("Errore caricamento bandi");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const confirmOpen = async () => {
    const slots = parseInt(slotsInput, 10);
    if (!Number.isFinite(slots) || slots < 1) {
      toast.error("Inserisci un numero di posti valido (≥ 1)");
      return;
    }
    setActioning(true);
    try {
      await api.post(`/quiz/admin/sessions/${opening.tier}/open`, { slots, note: noteInput || null });
      toast.success(`Bando ${opening.label} aperto con ${slots} ${slots === 1 ? "posto" : "posti"}`);
      setOpening(null);
      setNoteInput("");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore apertura bando");
    } finally {
      setActioning(false);
    }
  };

  const handleClose = async (tier, label) => {
    if (!window.confirm(`Chiudere il bando per ${label}? Gli attempt restano salvati per la classifica HR.`)) return;
    try {
      await api.post(`/quiz/admin/sessions/${tier}/close`);
      toast.success(`Bando ${label} chiuso`);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore chiusura");
    }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-7 w-7 animate-spin text-sheriff-gold" /></div>;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-heading font-bold text-xl text-[#F5E6B8]">Bandi Esami Agenti</h3>
        <p className="text-sm text-[#B8A269]">
          Apri un bando per un grado specifico indicando i posti disponibili. Gli agenti idonei potranno sostenere l&apos;esame UNA SOLA volta durante il bando. HR sceglierà chi promuovere dalla classifica.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {sessions.map((s) => {
          const isOpen = s.open;
          return (
            <div
              key={s.tier}
              data-testid={`session-card-${s.tier}`}
              className={`relative overflow-hidden rounded-2xl p-5 border-2 ${
                isOpen ? "border-sheriff-gold/60 bg-gradient-to-br from-sheriff-gold/15 to-[#0F2620]" : "border-sheriff-gold/20 bg-[#143830]"
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="uppercase-label text-sheriff-gold mb-1 flex items-center gap-1.5">
                    <Award className="h-3.5 w-3.5" /> {s.label}
                  </div>
                  <div className="text-xs text-[#B8A269]">Promuove a <span className="text-sheriff-gold font-semibold">{s.next_role}</span></div>
                </div>
                <span
                  className={`px-2 py-1 rounded-full text-[10px] uppercase tracking-[0.15em] font-bold border ${
                    isOpen ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-rose-500/15 text-rose-300 border-rose-500/30"
                  }`}
                  data-testid={`session-status-${s.tier}`}
                >
                  {isOpen ? "Aperto" : "Chiuso"}
                </span>
              </div>

              {isOpen ? (
                <>
                  <div className="bg-[#0A1F18]/60 rounded-xl p-3 mb-3 border border-sheriff-gold/25">
                    <div className="font-mono text-3xl font-black text-sheriff-gold">{s.slots}</div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-[#8B7B49] mt-1">
                      {s.slots === 1 ? "Posto disponibile" : "Posti disponibili"}
                    </div>
                  </div>
                  {s.opened_at && (
                    <div className="text-[10px] text-[#8B7B49] font-mono mb-3">
                      Aperto il {new Date(s.opened_at).toLocaleString("it-IT")}
                    </div>
                  )}
                  {s.note && <div className="text-xs text-[#D4C480] mb-3 italic">&laquo; {s.note} &raquo;</div>}
                  <Button
                    onClick={() => handleClose(s.tier, s.label)}
                    data-testid={`session-close-${s.tier}`}
                    variant="outline"
                    className="w-full border-rose-500/40 text-rose-300 hover:bg-rose-500/10 rounded-full uppercase-label font-bold"
                  >
                    <DoorClosed className="h-4 w-4 mr-2" /> Chiudi Bando
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-sm text-[#B8A269] mb-4">
                    Apri un bando per permettere agli agenti di sostenere l&apos;esame di promozione.
                  </p>
                  <Button
                    onClick={() => { setOpening(s); setSlotsInput("3"); setNoteInput(""); }}
                    data-testid={`session-open-${s.tier}`}
                    className="w-full bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] rounded-full uppercase-label font-bold"
                  >
                    <DoorOpen className="h-4 w-4 mr-2" /> Apri Bando
                  </Button>
                </>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!opening} onOpenChange={(o) => !o && setOpening(null)}>
        <DialogContent aria-describedby="open-session-desc" className="bg-gradient-to-br from-[#143830] to-[#0A1F18] border-sheriff-gold/40 text-[#F5E6B8] rounded-2xl">
          <DialogHeader>
            <div className="uppercase-label text-sheriff-gold mb-1">Apri Bando</div>
            <DialogTitle className="font-heading font-black text-2xl">
              {opening?.label} → {opening?.next_role}
            </DialogTitle>
            <DialogDescription id="open-session-desc" className="text-[#D4C480] pt-1 text-sm">
              Specifica il numero di posti disponibili. Una volta aperto, ogni agente idoneo potrà sostenere l&apos;esame UNA SOLA volta.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="uppercase-label text-sheriff-gold mb-1.5 block">Posti Disponibili</Label>
              <Input
                type="number"
                min="1"
                max="99"
                value={slotsInput}
                onChange={(e) => setSlotsInput(e.target.value)}
                data-testid="session-slots-input"
                className="bg-[#0F2620] border-sheriff-gold/30 rounded-xl text-[#F5E6B8]"
              />
            </div>
            <div>
              <Label className="uppercase-label text-sheriff-gold mb-1.5 block">Nota (opzionale)</Label>
              <Textarea
                rows={2}
                value={noteInput}
                onChange={(e) => setNoteInput(e.target.value)}
                placeholder="Es: Bando promozione Q1 2026"
                data-testid="session-note-input"
                className="bg-[#0F2620] border-sheriff-gold/30 rounded-xl text-[#F5E6B8]"
              />
            </div>
          </div>
          <DialogFooter className="flex flex-row gap-2 justify-end">
            <Button variant="outline" onClick={() => setOpening(null)} className="border-sheriff-gold/40 text-sheriff-gold rounded-full uppercase-label font-bold">
              <X className="h-4 w-4 mr-2" /> Annulla
            </Button>
            <Button
              onClick={confirmOpen}
              disabled={actioning}
              data-testid="session-open-confirm"
              className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] rounded-full uppercase-label font-bold"
            >
              {actioning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <DoorOpen className="h-4 w-4 mr-2" />}
              Conferma Apertura
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function QuizAdminPanel() {
  return <QuestionsAdmin />;
}

function QuestionsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed, {} = new, {id, ...} = edit
  const [filterTier, setFilterTier] = useState("vice_sceriffo");

  const reloadQuestions = async (tier = filterTier) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/quiz/admin/questions?exam_tier=${tier}`);
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore caricamento domande");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/quiz/admin/questions?exam_tier=${filterTier}`);
        if (!cancelled) setItems(data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore caricamento domande");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [filterTier]);

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questa domanda?")) return;
    try {
      await api.delete(`/quiz/admin/questions/${id}`);
      setItems((prev) => prev.filter((q) => q.id !== id));
      toast.success("Domanda eliminata");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore eliminazione");
    }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-7 w-7 animate-spin text-sheriff-gold" /></div>;

  return (
    <div className="space-y-4">
      <div className="bg-[#143830] border border-sheriff-gold/25 rounded-2xl p-4">
        <Label className="uppercase-label text-sheriff-gold mb-2 block">Tier dell&apos;Esame</Label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {EXAM_TIER_OPTIONS.map((t) => (
            <button
              type="button"
              key={t.key}
              data-testid={`tier-filter-${t.key}`}
              onClick={() => setFilterTier(t.key)}
              className={`text-left rounded-xl border px-3 py-2 transition-all ${
                filterTier === t.key
                  ? "border-sheriff-gold bg-sheriff-gold/15 text-sheriff-gold"
                  : "border-sheriff-gold/20 bg-[#0F2620] text-[#E8D89A] hover:border-sheriff-gold/60"
              }`}
            >
              <div className="font-bold text-sm">{t.label}</div>
              <div className="text-[10px] text-[#8B7B49] mt-0.5">Soglia: {t.threshold}%</div>
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-heading font-bold text-xl text-[#F5E6B8]">Domande · {tierLabel(filterTier)}</h3>
          <p className="text-sm text-[#B8A269]">Totale tier: <span className="font-mono text-sheriff-gold">{items.length}</span> domande</p>
        </div>
        <Button
          onClick={() => setEditing({ text: "", options: ["", "", "", ""], correct_index: 0, category: "", explanation: "", exam_tier: filterTier })}
          data-testid="quiz-add-btn"
          className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] rounded-full uppercase-label font-bold"
        >
          <Plus className="h-4 w-4 mr-2" /> Nuova Domanda
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="bg-[#0F2620] border border-sheriff-gold/20 rounded-2xl p-10 text-center">
          <GraduationCap className="h-10 w-10 text-sheriff-gold/50 mx-auto mb-3" />
          <p className="text-[#B8A269]">Nessuna domanda configurata per <span className="font-bold text-sheriff-gold">{tierLabel(filterTier)}</span>. Aggiungi la prima per attivare il questionario.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((q, i) => (
            <div key={q.id} className="bg-[#0F2620] border border-sheriff-gold/20 rounded-2xl p-5" data-testid={`quiz-q-row-${q.id}`}>
              <div className="flex items-start justify-between gap-4 mb-2">
                <div className="flex-1">
                  <div className="font-mono text-[10px] uppercase tracking-[0.18em] font-bold text-sheriff-gold/60 mb-1 flex items-center gap-2 flex-wrap">
                    <span>Domanda {String(i + 1).padStart(2, "0")}</span>
                    <span className="px-1.5 py-0.5 rounded bg-[#0A1F18] border border-sheriff-gold/30 text-sheriff-gold text-[9px]">
                      {tierLabel(q.exam_tier || "vice_sceriffo")}
                    </span>
                    {q.category && <span className="text-[#B8A269]">· {q.category}</span>}
                    <span className={`px-1.5 py-0.5 rounded text-[9px] ${q.type === "open" ? "bg-sheriff-gold/15 text-sheriff-gold" : "bg-emerald-500/15 text-emerald-300"}`}>
                      {q.type === "open" ? "APERTA" : "SCELTA MULTIPLA"}
                    </span>
                  </div>
                  <div className="font-heading font-bold text-[#F5E6B8] mb-2">{q.text}</div>
                  {q.type === "open" ? (
                    <div className="text-sm text-[#8B7B49] italic">Risposta libera · valutazione manuale HR</div>
                  ) : (
                    <ul className="grid sm:grid-cols-2 gap-1 text-sm">
                      {(q.options || []).map((opt, idx) => (
                        <li key={`${q.id}-opt-${idx}`} className={idx === q.correct_index ? "text-emerald-300" : "text-[#B8A269]"}>
                          <span className="font-mono font-bold mr-2">{String.fromCharCode(65 + idx)})</span>
                          {opt}
                          {idx === q.correct_index && <CheckCircle2 className="inline h-3.5 w-3.5 ml-1.5 -mt-0.5" />}
                        </li>
                      ))}
                    </ul>
                  )}
                  {q.explanation && (
                    <div className="mt-2 text-xs text-[#8B7B49] italic">{q.type === "open" ? "Nota HR" : "Spiegazione"}: {q.explanation}</div>
                  )}
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditing(q)}
                    data-testid={`quiz-edit-${q.id}`}
                    className="border-sheriff-gold/40 text-sheriff-gold hover:bg-sheriff-gold/10 rounded-full"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(q.id)}
                    data-testid={`quiz-delete-${q.id}`}
                    className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10 rounded-full"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <QuestionEditor
          key={editing?.id || "new"}
          open={editing !== null}
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reloadQuestions(); }}
        />
      )}
    </div>
  );
}

function QuestionEditor({ open, initial, onClose, onSaved }) {
  const [text, setText] = useState(initial?.text || "");
  const [type, setType] = useState(initial?.type || "multiple_choice");
  const [examTier, setExamTier] = useState(initial?.exam_tier || "vice_sceriffo");
  const [options, setOptions] = useState(
    initial?.options?.length ? [...initial.options] : ["", "", "", ""]
  );
  const [correctIndex, setCorrectIndex] = useState(initial?.correct_index ?? 0);
  const [category, setCategory] = useState(initial?.category || "");
  const [explanation, setExplanation] = useState(initial?.explanation || "");
  const [saving, setSaving] = useState(false);

  if (!open) return null;
  const isEdit = !!initial?.id;
  const isOpen = type === "open";

  const updateOption = (idx, v) => setOptions((prev) => prev.map((o, i) => (i === idx ? v : o)));
  const addOption = () => { if (options.length < 6) setOptions((prev) => [...prev, ""]); };
  const removeOption = (idx) => {
    if (options.length <= 2) return;
    setOptions((prev) => prev.filter((_, i) => i !== idx));
    if (correctIndex >= options.length - 1) setCorrectIndex(0);
  };

  const handleSave = async () => {
    if (!text.trim()) { toast.error("Inserisci il testo della domanda"); return; }
    setSaving(true);
    try {
      const payload = {
        text: text.trim(),
        type,
        exam_tier: examTier,
        category: category.trim() || null,
        explanation: explanation.trim() || null,
      };
      if (isOpen) {
        payload.options = null;
        payload.correct_index = null;
      } else {
        const cleanOpts = options.map((o) => o.trim()).filter(Boolean);
        if (cleanOpts.length < 2) { toast.error("Servono almeno 2 opzioni"); setSaving(false); return; }
        if (correctIndex >= cleanOpts.length) { toast.error("Indica la risposta corretta"); setSaving(false); return; }
        payload.options = cleanOpts;
        payload.correct_index = correctIndex;
      }
      if (isEdit) {
        await api.put(`/quiz/admin/questions/${initial.id}`, payload);
        toast.success("Domanda aggiornata");
      } else {
        await api.post("/quiz/admin/questions", payload);
        toast.success("Domanda creata");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-gradient-to-br from-[#143830] to-[#0A1F18] border-sheriff-gold/40 text-[#F5E6B8] rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="quiz-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-black text-2xl text-[#F5E6B8]">
            {isEdit ? "Modifica Domanda" : "Nuova Domanda"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <Label className="uppercase-label text-sheriff-gold mb-1.5 block">Tipo Domanda</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setType("multiple_choice")}
                data-testid="quiz-editor-type-mc"
                className={`text-left rounded-xl border px-4 py-3 transition-all ${
                  type === "multiple_choice"
                    ? "border-sheriff-gold bg-sheriff-gold/15 text-sheriff-gold"
                    : "border-sheriff-gold/20 bg-[#0F2620] text-[#E8D89A] hover:border-sheriff-gold/60"
                }`}
              >
                <div className="font-bold text-sm">Scelta Multipla</div>
                <div className="text-[10px] text-[#8B7B49] mt-1">Valutata automaticamente · concorre al punteggio</div>
              </button>
              <button
                type="button"
                onClick={() => setType("open")}
                data-testid="quiz-editor-type-open"
                className={`text-left rounded-xl border px-4 py-3 transition-all ${
                  type === "open"
                    ? "border-sheriff-gold bg-sheriff-gold/15 text-sheriff-gold"
                    : "border-sheriff-gold/20 bg-[#0F2620] text-[#E8D89A] hover:border-sheriff-gold/60"
                }`}
              >
                <div className="font-bold text-sm">Risposta Aperta</div>
                <div className="text-[10px] text-[#8B7B49] mt-1">Testo libero · valutata manualmente dalle HR</div>
              </button>
            </div>
          </div>
          <div>
            <Label className="uppercase-label text-sheriff-gold mb-1.5 block">Esame di Promozione</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {EXAM_TIER_OPTIONS.map((t) => (
                <button
                  type="button"
                  key={t.key}
                  onClick={() => setExamTier(t.key)}
                  data-testid={`quiz-editor-tier-${t.key}`}
                  className={`text-left rounded-xl border px-3 py-2 transition-all ${
                    examTier === t.key
                      ? "border-sheriff-gold bg-sheriff-gold/15 text-sheriff-gold"
                      : "border-sheriff-gold/20 bg-[#0F2620] text-[#E8D89A] hover:border-sheriff-gold/60"
                  }`}
                >
                  <div className="font-bold text-xs">{t.label}</div>
                  <div className="text-[9px] text-[#8B7B49] mt-0.5">Soglia {t.threshold}%</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="uppercase-label text-sheriff-gold mb-1.5 block">Testo Domanda</Label>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              placeholder="Es. Qual è il codice radio per richiesta di rinforzi immediati?"
              className="bg-[#0A1F18] border-sheriff-gold/30 rounded-xl"
              data-testid="quiz-editor-text"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="uppercase-label text-sheriff-gold mb-1.5 block">Categoria (opz.)</Label>
              <Input
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="Codici Radio, Codice Penale…"
                className="bg-[#0A1F18] border-sheriff-gold/30 rounded-xl"
                data-testid="quiz-editor-category"
              />
            </div>
          </div>
          {!isOpen && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="uppercase-label text-sheriff-gold">Opzioni (clicca il radio per la risposta corretta)</Label>
                {options.length < 6 && (
                  <Button type="button" size="sm" variant="outline" onClick={addOption} className="border-sheriff-gold/40 text-sheriff-gold rounded-full">
                    <Plus className="h-3 w-3 mr-1" /> Aggiungi
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                {options.map((opt, idx) => (
                  <div key={`opt-row-${idx}`} className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setCorrectIndex(idx)}
                      data-testid={`quiz-editor-correct-${idx}`}
                      className={`h-8 w-8 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                        correctIndex === idx
                          ? "bg-emerald-500 border-emerald-400 text-white"
                          : "border-sheriff-gold/40 text-sheriff-gold/40 hover:border-sheriff-gold"
                      }`}
                    >
                      {correctIndex === idx ? <CheckCircle2 className="h-4 w-4" /> : <span className="font-mono text-xs font-bold">{String.fromCharCode(65 + idx)}</span>}
                    </button>
                    <Input
                      value={opt}
                      onChange={(e) => updateOption(idx, e.target.value)}
                      placeholder={`Opzione ${String.fromCharCode(65 + idx)}`}
                      className="bg-[#0A1F18] border-sheriff-gold/30 rounded-xl flex-1"
                      data-testid={`quiz-editor-opt-${idx}`}
                    />
                    {options.length > 2 && (
                      <Button type="button" size="sm" variant="outline" onClick={() => removeOption(idx)} className="border-rose-500/40 text-rose-300 rounded-full h-8 w-8 p-0 shrink-0">
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {isOpen && (
            <div className="bg-[#0A1F18]/60 border border-sheriff-gold/20 rounded-xl p-4 text-sm text-[#D4C480]">
              L&apos;allievo risponderà con testo libero. Le risposte aperte <span className="font-bold text-sheriff-gold">non concorrono al punteggio automatico</span> e potranno essere lette dalle Risorse Umane nel dettaglio del tentativo.
            </div>
          )}
          <div>
            <Label className="uppercase-label text-sheriff-gold mb-1.5 block">Spiegazione / Note (opz.)</Label>
            <Textarea
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
              rows={2}
              placeholder={isOpen ? "Note interne per le HR sulla valutazione di questa risposta…" : "Es. Il codice 10-99 indica… (questa nota appare al candidato dopo aver inviato l'esame)"}
              className="bg-[#0A1F18] border-sheriff-gold/30 rounded-xl"
              data-testid="quiz-editor-explanation"
            />
          </div>
        </div>
        <DialogFooter className="gap-2 pt-3">
          <Button variant="outline" onClick={onClose} className="border-sheriff-gold/40 text-sheriff-gold rounded-full">Annulla</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] rounded-full uppercase-label font-bold" data-testid="quiz-editor-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            {isEdit ? "Salva Modifiche" : "Crea Domanda"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const WEEKDAY_OPTIONS = [
  { val: 0, label: "Lunedì" }, { val: 1, label: "Martedì" }, { val: 2, label: "Mercoledì" },
  { val: 3, label: "Giovedì" }, { val: 4, label: "Venerdì" }, { val: 5, label: "Sabato" },
  { val: 6, label: "Domenica" },
];
const emptyEvent = {
  title: "", description: "", location: "",
  recurrence_type: "weekly", weekday: 0, start_time: "22:00",
  start_at: "", duration_minutes: 60, is_active: true,
};

export function EventsAdminPanel() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} | event
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/events/admin/all");
      setEvents(data);
    } catch { toast.error("Errore caricamento eventi"); }
    finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const handleSave = async () => {
    if (!editing.title || editing.title.length < 2) {
      toast.error("Inserisci un titolo (almeno 2 caratteri)");
      return;
    }
    const desc = (editing.description || "").trim();
    if (desc.length < 10) {
      toast.error("La descrizione è obbligatoria (almeno 10 caratteri) — Discord la userà nel messaggio");
      return;
    }
    if (editing.recurrence_type === "weekly") {
      if (editing.weekday === null || editing.weekday === undefined || !editing.start_time) {
        toast.error("Per eventi settimanali servono giorno e ora");
        return;
      }
    } else if (!editing.start_at) {
      toast.error("Per eventi singoli serve la data");
      return;
    }
    const payload = {
      title: editing.title.trim(),
      description: desc,
      location: editing.location || null,
      recurrence_type: editing.recurrence_type,
      weekday: editing.recurrence_type === "weekly" ? Number(editing.weekday) : null,
      start_time: editing.recurrence_type === "weekly" ? editing.start_time : null,
      start_at: editing.recurrence_type === "once" ? new Date(editing.start_at).toISOString() : null,
      duration_minutes: editing.duration_minutes ? Number(editing.duration_minutes) : null,
      is_active: !!editing.is_active,
    };
    setSaving(true);
    try {
      if (editing.id) {
        await api.patch(`/events/${editing.id}`, payload);
        toast.success("Evento aggiornato");
      } else {
        await api.post("/events", payload);
        toast.success("Evento creato");
      }
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const handleDelete = async (ev) => {
    if (!confirm(`Eliminare definitivamente l'evento "${ev.title}"?`)) return;
    try {
      await api.delete(`/events/${ev.id}`);
      toast.success("Evento eliminato");
      reload();
    } catch { toast.error("Errore eliminazione"); }
  };

  return (
    <div className="space-y-5" data-testid="events-admin-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="uppercase-label text-sheriff-gold mb-1 flex items-center gap-2">
            <Calendar className="h-4 w-4" /> Gestione Eventi & Calendario
          </div>
          <p className="text-sm text-[#B8A269]">Crea briefing, addestramenti e operazioni speciali. Gli eventi attivi appaiono in homepage.</p>
        </div>
        <Button
          onClick={() => setEditing({ ...emptyEvent })}
          data-testid="create-event-btn"
          className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] font-bold rounded-full uppercase-label"
        >
          <Plus className="h-4 w-4 mr-1" /> Nuovo Evento
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-7 w-7 animate-spin text-sheriff-gold" /></div>
      ) : events.length === 0 ? (
        <div className="text-center py-12 bg-[#143830] border border-dashed border-sheriff-gold/25 rounded-xl">
          <Calendar className="h-10 w-10 text-sheriff-gold/40 mx-auto mb-3" />
          <p className="text-[#B8A269] mb-1">Nessun evento ancora creato</p>
          <p className="text-xs text-[#8B7B49]">Clicca &quot;Nuovo Evento&quot; per crearne uno</p>
        </div>
      ) : (
        <div className="bg-[#143830] border border-sheriff-gold/25 rounded-2xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-sheriff-gold/20">
                <TableHead className="text-sheriff-gold uppercase tracking-[0.1em] text-[10px]">Titolo</TableHead>
                <TableHead className="text-sheriff-gold uppercase tracking-[0.1em] text-[10px]">Quando</TableHead>
                <TableHead className="text-sheriff-gold uppercase tracking-[0.1em] text-[10px]">Luogo</TableHead>
                <TableHead className="text-sheriff-gold uppercase tracking-[0.1em] text-[10px]">Stato</TableHead>
                <TableHead className="text-right text-sheriff-gold uppercase tracking-[0.1em] text-[10px]">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((ev) => (
                <TableRow key={ev.id} data-testid={`event-row-${ev.id}`} className="border-sheriff-gold/10">
                  <TableCell className="text-[#F5E6B8] font-semibold">{ev.title}</TableCell>
                  <TableCell className="text-[#E8D89A] text-sm font-mono-codes">
                    {ev.recurrence_type === "weekly"
                      ? `Ogni ${ev.weekday_label} · ${ev.start_time}`
                      : ev.start_at ? new Date(ev.start_at).toLocaleString("it-IT") : "—"}
                  </TableCell>
                  <TableCell className="text-[#B8A269] text-sm">{ev.location || "—"}</TableCell>
                  <TableCell>
                    {ev.is_active
                      ? <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/40">Attivo</Badge>
                      : <Badge className="bg-rose-500/10 text-rose-300 border-rose-500/30">Disattivo</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="outline" size="sm" onClick={() => setEditing(ev)} data-testid={`edit-event-${ev.id}`} className="border-sheriff-gold/40 text-sheriff-gold hover:bg-sheriff-gold/10 rounded-full mr-2">
                      <Pencil className="h-3 w-3 mr-1" /> Modifica
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDelete(ev)} data-testid={`delete-event-${ev.id}`} className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10 rounded-full">
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl bg-gradient-to-br from-[#143830] to-[#0A1F18] border-sheriff-gold/40 text-[#F5E6B8] rounded-2xl max-h-[90vh] overflow-y-auto">
          {editing && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-[#F5E6B8]">
                  {editing.id ? "Modifica Evento" : "Nuovo Evento"}
                </DialogTitle>
                <DialogDescription className="text-[#B8A269]">
                  Compila tutti i dettagli — verranno utilizzati anche nelle notifiche Discord automatiche (30 / 15 / 5 / 1 min prima dell&apos;inizio).
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div>
                  <Label className="text-sheriff-gold uppercase-label">Titolo *</Label>
                  <Input data-testid="event-title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="es. Briefing Settimanale" />
                </div>
                <div>
                  <Label className="text-sheriff-gold uppercase-label flex items-center justify-between">
                    <span>Di cosa si tratta * <span className="text-[10px] text-[#8B7B49] normal-case font-normal">(comparirà su Discord)</span></span>
                    <span className="text-[10px] text-[#8B7B49] normal-case font-mono">{(editing.description || "").length}/2000</span>
                  </Label>
                  <Textarea
                    data-testid="event-description"
                    value={editing.description || ""}
                    onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                    rows={4}
                    placeholder="Esempio:&#10;Riunione settimanale del Dipartimento. Argomenti all'ordine del giorno:&#10;- Aggiornamenti operativi della scorsa settimana&#10;- Pianificazione pattuglie&#10;- Comunicazioni del Comando&#10;Presenza obbligatoria."
                  />
                </div>
                <div>
                  <Label className="text-sheriff-gold uppercase-label">Luogo</Label>
                  <Input data-testid="event-location" value={editing.location || ""} onChange={(e) => setEditing({ ...editing, location: e.target.value })} placeholder="es. Sala Briefing Centrale LSSD" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sheriff-gold uppercase-label">Tipo *</Label>
                    <Select value={editing.recurrence_type} onValueChange={(v) => setEditing({ ...editing, recurrence_type: v })}>
                      <SelectTrigger data-testid="event-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Settimanale (ricorrente)</SelectItem>
                        <SelectItem value="once">Singolo (data specifica)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sheriff-gold uppercase-label">Durata (min)</Label>
                    <Input data-testid="event-duration" type="number" min="0" max="1440" value={editing.duration_minutes || ""} onChange={(e) => setEditing({ ...editing, duration_minutes: e.target.value })} placeholder="60" />
                  </div>
                </div>
                {editing.recurrence_type === "weekly" ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sheriff-gold uppercase-label">Giorno *</Label>
                      <Select value={String(editing.weekday)} onValueChange={(v) => setEditing({ ...editing, weekday: Number(v) })}>
                        <SelectTrigger data-testid="event-weekday"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {WEEKDAY_OPTIONS.map((w) => (
                            <SelectItem key={w.val} value={String(w.val)}>{w.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sheriff-gold uppercase-label">Ora *</Label>
                      <Input data-testid="event-time" type="time" value={editing.start_time || "22:00"} onChange={(e) => setEditing({ ...editing, start_time: e.target.value })} />
                    </div>
                  </div>
                ) : (
                  <div>
                    <Label className="text-sheriff-gold uppercase-label">Data & Ora *</Label>
                    <Input data-testid="event-start-at" type="datetime-local" value={editing.start_at ? editing.start_at.slice(0, 16) : ""} onChange={(e) => setEditing({ ...editing, start_at: e.target.value })} />
                  </div>
                )}
                <div className="flex items-center gap-2 pt-2">
                  <Switch data-testid="event-active" checked={!!editing.is_active} onCheckedChange={(c) => setEditing({ ...editing, is_active: c })} />
                  <Label className="text-sheriff-gold uppercase-label cursor-pointer">Evento attivo (visibile in Area Agenti + notifiche Discord attive)</Label>
                </div>

                {/* Discord preview */}
                <EventDiscordPreview event={editing} />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditing(null)} className="border-sheriff-gold/40 text-sheriff-gold rounded-full">Annulla</Button>
                <Button onClick={handleSave} disabled={saving} data-testid="save-event-btn" className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] font-bold rounded-full">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Salva
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EventDiscordPreview({ event }) {
  const valid = event.title && (event.description || "").trim().length >= 10;
  return (
    <div className="bg-[#5865F2]/5 border border-[#5865F2]/30 rounded-xl p-4" data-testid="discord-preview">
      <div className="flex items-center gap-2 mb-2 text-[#5865F2]">
        <svg className="h-4 w-4" viewBox="0 0 71 55" fill="currentColor">
          <path d="M60.1 4.9A58.5 58.5 0 0 0 45.4.4a.2.2 0 0 0-.2.1c-.6 1.1-1.3 2.6-1.8 3.8a54 54 0 0 0-16.2 0c-.5-1.2-1.2-2.7-1.8-3.8a.2.2 0 0 0-.2-.1A58.4 58.4 0 0 0 10.5 4.9a.2.2 0 0 0-.1.1A59.7 59.7 0 0 0 .2 45.6a.2.2 0 0 0 .1.2c6.3 4.6 12.4 7.4 18.4 9.2a.2.2 0 0 0 .2-.1c1.4-1.9 2.7-3.9 3.7-6a.2.2 0 0 0-.1-.3c-2-.7-3.8-1.6-5.6-2.6a.2.2 0 0 1 0-.3l1.1-.9a.2.2 0 0 1 .2 0c11.7 5.3 24.4 5.3 36 0a.2.2 0 0 1 .2 0l1.1.9a.2.2 0 0 1 0 .3c-1.8 1-3.6 1.9-5.6 2.6a.2.2 0 0 0-.1.3c1.1 2.1 2.3 4.1 3.7 6a.2.2 0 0 0 .2.1c6-1.8 12.1-4.6 18.4-9.2a.2.2 0 0 0 .1-.2A59.4 59.4 0 0 0 60.2 5a.2.2 0 0 0-.1-.1ZM23.7 37.3c-3.6 0-6.6-3.3-6.6-7.4 0-4.1 3-7.4 6.6-7.4 3.7 0 6.7 3.4 6.6 7.4 0 4.1-3 7.4-6.6 7.4Zm24.5 0c-3.6 0-6.6-3.3-6.6-7.4 0-4.1 3-7.4 6.6-7.4 3.7 0 6.7 3.4 6.6 7.4 0 4.1-2.9 7.4-6.6 7.4Z"/>
        </svg>
        <span className="uppercase-label text-[10px] font-bold text-[#5865F2]">Anteprima messaggio Discord</span>
      </div>
      {!valid ? (
        <p className="text-xs text-[#8B7B49] italic">Compila titolo e descrizione (almeno 10 caratteri) per vedere l&apos;anteprima.</p>
      ) : (
        <div className="bg-[#2B2D31] rounded-lg p-3 border-l-4 border-sheriff-gold text-[#DBDEE1] text-xs space-y-1.5">
          <div className="text-[11px] font-semibold text-white">@everyone 📣 TRA 15 MINUTI</div>
          <div className="font-heading font-bold text-[#F5E6B8]">🚔 {event.title}</div>
          <div className="text-[#DBDEE1] whitespace-pre-wrap line-clamp-4">{event.description}</div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1 text-[10px] uppercase tracking-[0.1em]">
            {event.recurrence_type === "weekly" && editingTimePreview(event)}
            {event.location && <span className="text-[#B8A269]">📍 {event.location}</span>}
            {event.duration_minutes && <span className="text-[#B8A269]">⌛ {event.duration_minutes} min</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function editingTimePreview(ev) {
  const wd = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];
  return <span className="text-[#B8A269]">🕘 Ogni {wd[Number(ev.weekday) || 0]} alle {ev.start_time}</span>;
}


// ============================================================
// ANNOUNCEMENTS PANEL — Comando posta annunci su Discord
// ============================================================
const FORMATTING_HINTS = [
  { syntax: "**testo**", label: "Grassetto", icon: Bold },
  { syntax: "*testo*", label: "Corsivo", icon: Italic },
  { syntax: "||testo||", label: "Spoiler", icon: EyeOff },
];

export function AnnouncementsPanel() {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [sending, setSending] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [serverEmojis, setServerEmojis] = useState([]);
  const [emojiLoading, setEmojiLoading] = useState(false);
  const [emojiError, setEmojiError] = useState(null);
  const [emojiQuery, setEmojiQuery] = useState("");
  // Tag ruoli + everyone
  const [mentionOpen, setMentionOpen] = useState(false);
  const [serverRoles, setServerRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState(null);
  const [rolesQuery, setRolesQuery] = useState("");
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [mentionEveryone, setMentionEveryone] = useState(false);
  const titleRef = useRef(null);
  const descRef = useRef(null);

  const insertAtCursorIn = (ref, value, setValue, text) => {
    const el = ref.current;
    if (!el) { setValue((p) => p + text); return; }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const newVal = value.slice(0, start) + text + value.slice(end);
    setValue(newVal);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + text.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const wrapIn = (ref, value, setValue, before, after = before) => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = value.slice(start, end) || "testo";
    const newVal = value.slice(0, start) + before + selected + after + value.slice(end);
    setValue(newVal);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + before.length, start + before.length + selected.length);
    });
  };

  const insertHeadingIn = (ref, value, setValue, level = 1) => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const prefix = "#".repeat(level) + " ";
    const before = value.slice(0, start);
    const after = value.slice(start);
    const lineStart = before.lastIndexOf("\n") + 1;
    const linePrefix = before.slice(0, lineStart);
    const currentLine = before.slice(lineStart);
    const stripped = currentLine.replace(/^#{1,3}\s/, "");
    const newVal = linePrefix + prefix + stripped + after;
    setValue(newVal);
    requestAnimationFrame(() => {
      el.focus();
      const newPos = linePrefix.length + prefix.length + stripped.length;
      el.setSelectionRange(newPos, newPos);
    });
  };

  // Carica le emoji custom del server alla prima apertura del picker
  useEffect(() => {
    if (!emojiOpen || serverEmojis.length > 0 || emojiLoading) return;
    setEmojiLoading(true);
    setEmojiError(null);
    api.get("/discord/server-emojis")
      .then(({ data }) => {
        if (data?.ok) {
          setServerEmojis(data.emojis || []);
        } else {
          setEmojiError(data?.reason || "Impossibile caricare le emoji");
        }
      })
      .catch((e) => setEmojiError(e?.response?.data?.detail || "Errore caricamento emoji"))
      .finally(() => setEmojiLoading(false));
  }, [emojiOpen, serverEmojis.length, emojiLoading]);

  // Carica i ruoli del server alla prima apertura del picker menzioni
  useEffect(() => {
    if (!mentionOpen || serverRoles.length > 0 || rolesLoading) return;
    setRolesLoading(true);
    setRolesError(null);
    api.get("/discord/server-roles")
      .then(({ data }) => {
        if (data?.ok) {
          setServerRoles(data.roles || []);
        } else {
          setRolesError(data?.reason || "Impossibile caricare i ruoli");
        }
      })
      .catch((e) => setRolesError(e?.response?.data?.detail || "Errore caricamento ruoli"))
      .finally(() => setRolesLoading(false));
  }, [mentionOpen, serverRoles.length, rolesLoading]);

  const toggleRole = (rid) => {
    setSelectedRoleIds((prev) => prev.includes(rid) ? prev.filter((x) => x !== rid) : [...prev, rid]);
  };

  const insertAtCursor = (text) => insertAtCursorIn(descRef, description, setDescription, text);
  const wrap = (before, after = before) => wrapIn(descRef, description, setDescription, before, after);
  /** Inserisce un heading: aggiunge `# ` all'inizio della riga corrente (o nuova riga vuota). */
  const insertHeading = (level = 1) => insertHeadingIn(descRef, description, setDescription, level);

  const submit = async () => {
    if (title.trim().length < 2) { toast.error("Titolo troppo corto"); return; }
    if (description.trim().length < 2) { toast.error("Descrizione troppo corta"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/announcements/discord", {
        title: title.trim(),
        description: description.trim(),
        mention_role_ids: selectedRoleIds,
        mention_everyone: mentionEveryone,
      });
      toast.success(`Annuncio pubblicato · ${data.posted_by}`, { duration: 8000 });
      setTitle("");
      setDescription("");
      setSelectedRoleIds([]);
      setMentionEveryone(false);
    } catch (e) {
      toast.error(`Errore: ${e?.response?.data?.detail || "invio fallito"}`);
    } finally {
      setSending(false);
    }
  };

  const filteredEmojis = serverEmojis.filter((e) => !emojiQuery || e.name.toLowerCase().includes(emojiQuery.toLowerCase()));
  const filteredRoles = serverRoles.filter((r) => !rolesQuery || r.name.toLowerCase().includes(rolesQuery.toLowerCase()));
  const totalMentions = selectedRoleIds.length + (mentionEveryone ? 1 : 0);

  return (
    <div className="grid lg:grid-cols-5 gap-6" data-testid="announcements-panel">
      <div className="lg:col-span-3 space-y-4">
        <div>
          <div className="flex items-center justify-between mb-1.5 flex-wrap gap-2">
            <Label className="uppercase-label text-sheriff-gold">Titolo</Label>
            <div className="flex gap-1.5 flex-wrap">
              <Button onClick={() => insertHeadingIn(titleRef, title, setTitle, 1)} variant="ghost" size="sm" type="button" className="text-sheriff-gold hover:bg-sheriff-gold/15 hover:text-[#F5E6B8] text-xs h-7" title="Titolo gigante (# testo)" data-testid="title-format-h1">
                <Heading1 className="h-3.5 w-3.5 mr-1" /> Gigante
              </Button>
              <Button onClick={() => wrapIn(titleRef, title, setTitle, "**", "**")} variant="ghost" size="sm" type="button" className="text-sheriff-gold hover:bg-sheriff-gold/15 hover:text-[#F5E6B8] text-xs h-7" title="Grassetto" data-testid="title-format-bold">
                <Bold className="h-3.5 w-3.5 mr-1" /> Grassetto
              </Button>
              <Button onClick={() => wrapIn(titleRef, title, setTitle, "*", "*")} variant="ghost" size="sm" type="button" className="text-sheriff-gold hover:bg-sheriff-gold/15 hover:text-[#F5E6B8] text-xs h-7" title="Corsivo" data-testid="title-format-italic">
                <Italic className="h-3.5 w-3.5 mr-1" /> Corsivo
              </Button>
              <Button onClick={() => wrapIn(titleRef, title, setTitle, "||", "||")} variant="ghost" size="sm" type="button" className="text-sheriff-gold hover:bg-sheriff-gold/15 hover:text-[#F5E6B8] text-xs h-7" title="Spoiler" data-testid="title-format-spoiler">
                <EyeOff className="h-3.5 w-3.5 mr-1" /> Spoiler
              </Button>
            </div>
          </div>
          <Input
            ref={titleRef}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Es. # ALLERTA OPERATIVA · **Nuove direttive**"
            className="bg-[#0A1F18] border-sheriff-gold/30 text-[#F5E6B8] font-mono-codes"
            data-testid="announcement-title-input"
          />
          <div className="text-[10px] text-[#B8A269] mt-1">
            💡 Puoi usare <code className="text-sheriff-gold">#</code> per fare il titolo <strong>gigante</strong> + altri marker Discord. Verrà reso formattato come prima riga dell&apos;embed.
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5 flex-wrap gap-2">
            <Label className="uppercase-label text-sheriff-gold">Descrizione</Label>
            <div className="flex gap-1.5 flex-wrap">
              <Button onClick={() => insertHeading(1)} variant="ghost" size="sm" type="button" className="text-sheriff-gold hover:bg-sheriff-gold/15 hover:text-[#F5E6B8] text-xs h-7" title="Titolo gigante (# testo)" data-testid="format-h1">
                <Heading1 className="h-3.5 w-3.5 mr-1" /> Gigante
              </Button>
              {FORMATTING_HINTS.map((h) => {
                const Icon = h.icon;
                const [before, after] = h.syntax.split("testo");
                return (
                  <Button key={h.label} onClick={() => wrap(before, after)} variant="ghost" size="sm" type="button" className="text-sheriff-gold hover:bg-sheriff-gold/15 hover:text-[#F5E6B8] text-xs h-7" title={h.label} data-testid={`format-${h.label.toLowerCase()}`}>
                    <Icon className="h-3.5 w-3.5 mr-1" /> {h.label}
                  </Button>
                );
              })}
              <Button onClick={() => setEmojiOpen((o) => !o)} variant="ghost" size="sm" type="button" className={`hover:bg-sheriff-gold/15 hover:text-[#F5E6B8] text-xs h-7 ${emojiOpen ? "text-[#F5E6B8] bg-sheriff-gold/15" : "text-sheriff-gold"}`} title="Emoji del server" data-testid="format-emoji">
                <Smile className="h-3.5 w-3.5 mr-1" /> Emoji
              </Button>
              <Button onClick={() => setMentionOpen((o) => !o)} variant="ghost" size="sm" type="button" className={`hover:bg-sheriff-gold/15 hover:text-[#F5E6B8] text-xs h-7 ${mentionOpen ? "text-[#F5E6B8] bg-sheriff-gold/15" : "text-sheriff-gold"}`} title="Tagga ruoli o @everyone" data-testid="format-mention">
                <AtSign className="h-3.5 w-3.5 mr-1" /> Menziona{totalMentions > 0 && <span className="ml-1 bg-sheriff-gold text-[#0A1F18] rounded-full px-1.5 text-[10px] font-bold">{totalMentions}</span>}
              </Button>
            </div>
          </div>
          <Textarea
            ref={descRef}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={10}
            placeholder="Scrivi qui il corpo dell'annuncio. Usa # per il titolo gigante, **grassetto**, *corsivo*, ||spoiler||."
            className="bg-[#0A1F18] border-sheriff-gold/30 text-[#F5E6B8] font-mono-codes text-sm"
            data-testid="announcement-description-input"
          />

          {/* Emoji picker */}
          {emojiOpen && (
            <div className="mt-2 bg-[#0A1F18] border border-sheriff-gold/30 rounded-lg p-3" data-testid="emoji-picker">
              <div className="flex items-center gap-2 mb-2">
                <Search className="h-3.5 w-3.5 text-sheriff-gold" />
                <input
                  value={emojiQuery}
                  onChange={(e) => setEmojiQuery(e.target.value)}
                  placeholder="Cerca emoji per nome…"
                  className="flex-1 bg-transparent border-0 outline-0 text-sm text-[#F5E6B8] placeholder:text-[#8B7B49]"
                  data-testid="emoji-search-input"
                />
                <button onClick={() => setEmojiOpen(false)} className="text-[#8B7B49] hover:text-rose-300" title="Chiudi">
                  <X className="h-4 w-4" />
                </button>
              </div>
              {emojiLoading && <div className="text-xs text-[#D4C480] flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Caricamento emoji…</div>}
              {emojiError && <div className="text-xs text-rose-300">⚠️ {emojiError}</div>}
              {!emojiLoading && !emojiError && filteredEmojis.length === 0 && (
                <div className="text-xs text-[#B8A269]">Nessuna emoji {emojiQuery ? `corrispondente a "${emojiQuery}"` : "trovata"}.</div>
              )}
              {!emojiLoading && !emojiError && filteredEmojis.length > 0 && (
                <div className="grid grid-cols-8 sm:grid-cols-10 md:grid-cols-12 gap-1 max-h-56 overflow-y-auto pr-1">
                  {filteredEmojis.map((e) => (
                    <button
                      key={e.id}
                      onClick={() => insertAtCursor(e.syntax)}
                      className="aspect-square flex items-center justify-center rounded hover:bg-sheriff-gold/15 transition-colors p-1"
                      title={`:${e.name}: ${e.animated ? "(animata)" : ""}`}
                      data-testid={`emoji-${e.name}`}
                    >
                      <img src={e.url} alt={e.name} className="w-full h-full object-contain" loading="lazy" />
                    </button>
                  ))}
                </div>
              )}
              {!emojiLoading && !emojiError && (
                <div className="text-[10px] text-[#B8A269] mt-2">
                  {filteredEmojis.length} di {serverEmojis.length} emoji · clicca per inserire la sintassi <code className="text-sheriff-gold">{"<:nome:id>"}</code>
                </div>
              )}
            </div>
          )}

          {/* Menziona picker */}
          {mentionOpen && (
            <div className="mt-2 bg-[#0A1F18] border border-sheriff-gold/30 rounded-lg p-3" data-testid="mention-picker">
              <div className="flex items-center gap-2 mb-2">
                <AtSign className="h-3.5 w-3.5 text-sheriff-gold" />
                <input
                  value={rolesQuery}
                  onChange={(e) => setRolesQuery(e.target.value)}
                  placeholder="Cerca ruolo per nome…"
                  className="flex-1 bg-transparent border-0 outline-0 text-sm text-[#F5E6B8] placeholder:text-[#8B7B49]"
                  data-testid="mention-search-input"
                />
                <button onClick={() => setMentionOpen(false)} className="text-[#8B7B49] hover:text-rose-300" title="Chiudi">
                  <X className="h-4 w-4" />
                </button>
              </div>
              {/* @everyone toggle */}
              <button
                onClick={() => setMentionEveryone((v) => !v)}
                className={`w-full text-left mb-2 px-2.5 py-1.5 rounded border transition-colors ${mentionEveryone ? "bg-rose-500/15 border-rose-400/50 text-rose-200" : "bg-[#143830] border-sheriff-gold/20 text-[#D4C480] hover:bg-sheriff-gold/10"}`}
                data-testid="mention-everyone-toggle"
              >
                <span className="inline-flex items-center gap-2 text-sm font-bold">
                  <span className={`inline-flex h-3.5 w-3.5 items-center justify-center rounded-sm border ${mentionEveryone ? "bg-rose-400/80 border-rose-400" : "bg-[#0A1F18] border-sheriff-gold/40"}`}>
                    {mentionEveryone && <CheckCircle2 className="h-3 w-3 text-[#0A1F18]" strokeWidth={3} />}
                  </span>
                  @everyone
                  <span className="text-[10px] text-[#B8A269] font-normal ml-1">(notifica TUTTI i membri del server)</span>
                </span>
              </button>
              {rolesLoading && <div className="text-xs text-[#D4C480] flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Caricamento ruoli…</div>}
              {rolesError && <div className="text-xs text-rose-300">⚠️ {rolesError}</div>}
              {!rolesLoading && !rolesError && filteredRoles.length === 0 && (
                <div className="text-xs text-[#B8A269]">Nessun ruolo {rolesQuery ? `corrispondente a "${rolesQuery}"` : "trovato"}.</div>
              )}
              {!rolesLoading && !rolesError && filteredRoles.length > 0 && (
                <div className="max-h-56 overflow-y-auto pr-1 space-y-1">
                  {filteredRoles.map((r) => {
                    const checked = selectedRoleIds.includes(r.id);
                    return (
                      <button
                        key={r.id}
                        onClick={() => toggleRole(r.id)}
                        className={`w-full text-left px-2.5 py-1.5 rounded border transition-colors flex items-center gap-2 ${checked ? "bg-sheriff-gold/15 border-sheriff-gold/50 text-[#F5E6B8]" : "bg-[#143830] border-sheriff-gold/15 text-[#D4C480] hover:bg-sheriff-gold/10"}`}
                        data-testid={`mention-role-${r.name}`}
                      >
                        <span className={`inline-flex h-3.5 w-3.5 items-center justify-center rounded-sm border ${checked ? "bg-sheriff-gold border-sheriff-gold" : "bg-[#0A1F18] border-sheriff-gold/40"}`}>
                          {checked && <CheckCircle2 className="h-3 w-3 text-[#0A1F18]" strokeWidth={3} />}
                        </span>
                        <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: r.color_hex || "#888" }} />
                        <span className="text-sm font-medium flex-1">{r.name}</span>
                        {!r.mentionable && (
                          <span className="text-[9px] text-amber-300 uppercase tracking-widest">non-menzionabile</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              {!rolesLoading && !rolesError && (
                <div className="text-[10px] text-[#B8A269] mt-2 leading-relaxed">
                  {selectedRoleIds.length > 0 || mentionEveryone ? (
                    <>Selezionati: <span className="text-sheriff-gold font-bold">{totalMentions}</span> · verranno inseriti come tag nel content del messaggio Discord.</>
                  ) : (
                    <>Seleziona i ruoli da taggare. I ruoli marcati &quot;non-menzionabili&quot; non genereranno notifica push (richiede permessi).</>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="text-xs text-[#B8A269] mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
            <span><code className="text-sheriff-gold">#</code> titolo gigante</span>
            <span><code className="text-sheriff-gold">##</code> titolo medio</span>
            <span><code className="text-sheriff-gold">**testo**</code> grassetto</span>
            <span><code className="text-sheriff-gold">*testo*</code> corsivo</span>
            <span><code className="text-sheriff-gold">||testo||</code> spoiler</span>
            <span><code className="text-sheriff-gold">__testo__</code> sottolineato</span>
            <span><code className="text-sheriff-gold">~~testo~~</code> barrato</span>
            <span><code className="text-sheriff-gold">`code`</code> codice</span>
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            onClick={submit}
            disabled={sending || title.trim().length < 2 || description.trim().length < 2}
            className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] font-bold uppercase tracking-[0.12em] rounded-full px-6 disabled:opacity-50"
            data-testid="announcement-send-btn"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Megaphone className="h-4 w-4 mr-2" />}
            Pubblica Annuncio
          </Button>
        </div>
      </div>

      <div className="lg:col-span-2">
        <Label className="uppercase-label text-sheriff-gold mb-1.5 block">Anteprima Discord</Label>
        <div className="bg-[#36393F] rounded-lg p-3 border-l-4 border-sheriff-gold" data-testid="announcement-preview">
          {totalMentions > 0 && (
            <div className="mb-2 flex flex-wrap gap-1.5">
              {mentionEveryone && (
                <span className="bg-rose-400/30 text-rose-200 rounded px-1.5 py-0.5 text-sm font-semibold">@everyone</span>
              )}
              {selectedRoleIds.map((rid) => {
                const r = serverRoles.find((x) => x.id === rid);
                if (!r) return null;
                return (
                  <span key={rid} className="rounded px-1.5 py-0.5 text-sm font-semibold" style={{ background: `${r.color_hex || "#888"}30`, color: r.color_hex || "#DCDDDE" }}>
                    @{r.name}
                  </span>
                );
              })}
            </div>
          )}
          <div className="font-bold text-white mb-1 break-words">
            {title ? renderTitlePreview(title, serverEmojis) : "Titolo annuncio"}
          </div>
          <div className="text-sm text-[#DCDDDE] break-words leading-relaxed">
            {description ? renderDiscordPreview(description, serverEmojis) : <span className="text-[#72767D] italic">L&apos;anteprima del corpo apparirà qui…</span>}
          </div>
          <div className="text-[10px] text-[#72767D] mt-3 border-t border-white/10 pt-2">
            📢 Annuncio LSSD · Pubblicato da <em>(grado + nome operatore)</em>
          </div>
        </div>
        <div className="text-[10px] text-[#B8A269] mt-2 leading-relaxed">
          ℹ️ Il sistema aggiunge automaticamente il tuo <span className="text-sheriff-gold font-semibold">grado e nome operatore</span> nel footer.
        </div>
      </div>
    </div>
  );
}

/** Render del titolo nell'anteprima Discord. Stesso markdown del body. */
function renderTitlePreview(text, emojiList = []) {
  const emojiById = new Map(emojiList.map((e) => [e.id, e]));
  // Heading detect
  let inner = text;
  let HeadingSize = null;
  if (inner.startsWith("### ")) { HeadingSize = "text-lg"; inner = inner.slice(4); }
  else if (inner.startsWith("## ")) { HeadingSize = "text-xl"; inner = inner.slice(3); }
  else if (inner.startsWith("# ")) { HeadingSize = "text-2xl"; inner = inner.slice(2); }
  const content = renderInline(inner, emojiById);
  if (HeadingSize) return <span className={`${HeadingSize} font-extrabold text-white leading-tight`}>{content}</span>;
  return content;
}

/** Render minimal Discord markdown:
 *  # H1 / ## H2 / ### H3 a inizio riga, **bold**, *italic*, ||spoiler||, __underline__, ~~strike~~, `code`,
 *  emoji custom <:name:id> e <a:name:id>. Risolve l'URL emoji dalla lista server. */
function renderDiscordPreview(text, emojiList = []) {
  const emojiById = new Map(emojiList.map((e) => [e.id, e]));
  const lines = text.split("\n");
  return lines.map((line, lineIdx) => {
    let inner = line;
    let HeadingTag = null;
    if (inner.startsWith("### ")) { HeadingTag = "h3"; inner = inner.slice(4); }
    else if (inner.startsWith("## ")) { HeadingTag = "h2"; inner = inner.slice(3); }
    else if (inner.startsWith("# ")) { HeadingTag = "h1"; inner = inner.slice(2); }
    const content = renderInline(inner, emojiById);
    if (HeadingTag === "h1") return <div key={lineIdx} className="text-2xl font-extrabold text-white leading-tight mt-2 first:mt-0">{content}</div>;
    if (HeadingTag === "h2") return <div key={lineIdx} className="text-xl font-bold text-white leading-tight mt-2 first:mt-0">{content}</div>;
    if (HeadingTag === "h3") return <div key={lineIdx} className="text-lg font-bold text-white leading-tight mt-2 first:mt-0">{content}</div>;
    return <div key={lineIdx} className="whitespace-pre-wrap">{content.length > 0 ? content : "\u00A0"}</div>;
  });
}

function renderInline(text, emojiById) {
  const nodes = [];
  const tokens = text.split(/(<a?:[^:>]+:\d+>|\*\*[^*]+\*\*|\*[^*]+\*|\|\|[^|]+\|\||__[^_]+__|~~[^~]+~~|`[^`]+`)/g);
  tokens.forEach((tk, i) => {
    if (!tk) return;
    const emojiMatch = tk.match(/^<a?:([^:>]+):(\d+)>$/);
    if (emojiMatch) {
      const id = emojiMatch[2];
      const name = emojiMatch[1];
      const found = emojiById.get(id);
      const url = found?.url || `https://cdn.discordapp.com/emojis/${id}.${tk.startsWith("<a") ? "gif" : "png"}`;
      nodes.push(<img key={i} src={url} alt={name} title={`:${name}:`} className="inline-block w-5 h-5 align-text-bottom mx-0.5" />);
    }
    else if (tk.startsWith("**") && tk.endsWith("**")) nodes.push(<strong key={i} className="font-bold">{tk.slice(2, -2)}</strong>);
    else if (tk.startsWith("__") && tk.endsWith("__")) nodes.push(<u key={i}>{tk.slice(2, -2)}</u>);
    else if (tk.startsWith("~~") && tk.endsWith("~~")) nodes.push(<s key={i} className="opacity-70">{tk.slice(2, -2)}</s>);
    else if (tk.startsWith("||") && tk.endsWith("||")) nodes.push(<span key={i} className="bg-[#202225] text-[#202225] hover:text-[#DCDDDE] rounded px-1 cursor-pointer transition-colors">{tk.slice(2, -2)}</span>);
    else if (tk.startsWith("*") && tk.endsWith("*")) nodes.push(<em key={i} className="italic">{tk.slice(1, -1)}</em>);
    else if (tk.startsWith("`") && tk.endsWith("`")) nodes.push(<code key={i} className="bg-[#2F3136] text-[#DCDDDE] px-1 rounded text-[12px] font-mono">{tk.slice(1, -1)}</code>);
    else nodes.push(<span key={i}>{tk}</span>);
  });
  return nodes;
}

function BadgeNumberInput({ user, onSave }) {
  const [val, setVal] = useState(user?.badge_number || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(user?.badge_number || ""); }, [user?.badge_number]);
  const commit = async () => {
    if ((val || "").trim() === (user?.badge_number || "")) return;
    setSaving(true);
    try { await onSave(val.trim() || null); }
    finally { setSaving(false); }
  };
  return (
    <input
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
      placeholder="—"
      className={`w-20 mx-auto text-center bg-[#0F2620] border border-sheriff-gold/30 rounded px-2 py-1 text-xs font-mono text-sheriff-gold focus:outline-none focus:border-sheriff-gold ${saving ? "opacity-50" : ""}`}
      disabled={saving}
      data-testid={`badge-number-${user.id}`}
      maxLength={20}
    />
  );
}


